/**
 * TQ #7976 (C2 Unified-Cockpit Phase 7):
 * Per-User Soul-Addendum Editor im Personal-Modus (with.it-sce.de).
 *
 * Lädt / speichert user_soul_addendum via GET/PUT /api/me/soul.
 * Analog zu AriaOrgSettings Persona-Tab aber für den einzelnen User.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { api } from '../services/api'

export default function MySoulEditor() {
  const { token } = useAuth()

  const [text, setText] = useState('')
  const [original, setOriginal] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedMsg, setSavedMsg] = useState<string | null>(null)
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const load = useCallback(async () => {
    if (!token) return
    api.setToken(token)
    setLoading(true)
    setError(null)
    try {
      const res = await api.getMyUserSoul()
      setText(res.soul_addendum ?? '')
      setOriginal(res.soul_addendum ?? '')
    } catch {
      setError('Laden fehlgeschlagen.')
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => { void load() }, [load])

  const handleSave = useCallback(async () => {
    if (!token) return
    api.setToken(token)
    setSaving(true)
    setError(null)
    setSavedMsg(null)
    try {
      await api.putMyUserSoul(text)
      setOriginal(text)
      setSavedMsg('Gespeichert.')
      if (savedTimer.current) clearTimeout(savedTimer.current)
      savedTimer.current = setTimeout(() => setSavedMsg(null), 3000)
    } catch {
      setError('Speichern fehlgeschlagen.')
    } finally {
      setSaving(false)
    }
  }, [token, text])

  const isDirty = text !== original

  return (
    <div className="aria-settings-block">
      <section className="aria-settings-card">
        <h2>Mein Aria-Charakter</h2>
        <p className="aria-settings-subtitle">
          Beschreibe Aria wer du bist, wie du arbeitest und was sie wissen
          soll — sie bezieht diese Informationen in jede Konversation ein.
          Dieser Text gilt nur für dich (nicht für andere Nutzer).
        </p>

        {loading ? (
          <p className="aria-settings-note">Wird geladen…</p>
        ) : (
          <>
            <textarea
              className="aria-soul-textarea"
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder={
                'Ich bin … und arbeite hauptsächlich an …\n' +
                'Aria soll wissen: …\n' +
                'Mein bevorzugter Kommunikationsstil: …'
              }
              rows={12}
              disabled={saving}
              maxLength={8192}
            />
            <div className="aria-soul-footer">
              <span className="aria-settings-note">{text.length} / 8192 Zeichen</span>
              <div className="aria-soul-actions">
                {isDirty && !saving && (
                  <button
                    className="aria-soul-btn-cancel"
                    onClick={() => setText(original)}
                  >
                    Zurücksetzen
                  </button>
                )}
                <button
                  className="aria-soul-btn-save"
                  onClick={() => void handleSave()}
                  disabled={saving || !isDirty}
                >
                  {saving ? 'Wird gespeichert…' : 'Speichern'}
                </button>
              </div>
            </div>
            {savedMsg && <div className="aria-settings-success">{savedMsg}</div>}
            {error && <div className="aria-settings-error">{error}</div>}
          </>
        )}
      </section>
    </div>
  )
}
